<template>
  <fieldset class="form-section-group mt-4 mb-4">
    <legend class="section-legend" id="domain-section-legend">
      Search by Domain or Family
      <span class="legend-note">(proteins must match every row)</span>
    </legend>
    <div class="ml-4 mt-2">
      <div v-for="(row, index) in value" :key="row.id" class="domain-row">
        <div class="row">
          <div class="col-sm-4">
            <label
              class="text-left fw-bold form-label pt-2"
              style="height: 20px; color: black"
              :for="`domain_text_${index}`"
            >
              Protein has
            </label>
          </div>
          <div class="col-lg-8 col-md-12 col-sm-12 d-flex flex-wrap align-items-center">
            <select
              :id="`domain_comparison_${index}`"
              class="select-medium comparison"
              :value="row.comparison"
              :aria-label="`Domain row ${index + 1} number of copies comparison`"
              @change="update(index, { comparison: $event.target.value })"
            >
              <option
                v-for="option in comparisonsFor(row)"
                :key="option.value"
                :value="option.value"
              >
                {{ option.text }}
              </option>
            </select>
            <input
              v-if="row.comparison !== 'none'"
              :id="`domain_count_${index}`"
              type="number"
              min="1"
              class="count"
              :value="row.count"
              :disabled="kindOf(row) === 'name'"
              :title="
                kindOf(row) === 'name'
                  ? 'To count copies, use an ID such as PF03140'
                  : ''
              "
              :aria-label="`Domain row ${index + 1} number of copies`"
              @input="update(index, { count: $event.target.value })"
            />
            <span class="connector">{{ connector(row) }}</span>
            <input
              :id="`domain_text_${index}`"
              type="text"
              class="text"
              :value="row.text"
              :aria-label="`Domain row ${index + 1} name or ID`"
              autocomplete="off"
              @input="update(index, { text: $event.target.value })"
              @keyup.enter="$emit('on-enter', $event)"
            />
            <button
              v-if="value.length > 1"
              type="button"
              class="btn btn-link btn-sm"
              :aria-label="`Remove domain row ${index + 1}`"
              @click="remove(index)"
            >
              Remove
            </button>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-4"></div>
          <div class="col-lg-8 col-md-12 col-sm-12 reading" aria-live="polite">
            <span>{{ readings[index].note }}</span>
            <span v-if="readings[index].warning" class="reading-warning">
              {{ readings[index].warning }}
            </span>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-4"></div>
        <div class="col-lg-8 col-md-12 col-sm-12 text-left">
          <button
            v-if="value.length < maxRows"
            id="domain_add_row"
            type="button"
            class="btn btn-link btn-sm pl-0"
            @click="add"
          >
            + Add another domain
          </button>
          <div v-if="error" class="domain-error" role="alert">{{ error }}</div>
        </div>
      </div>
    </div>
  </fieldset>
</template>

<script>
import {
  COMPARISONS,
  MAX_DOMAIN_ROWS,
  domainTextKind,
  newDomainRow,
  readingFor,
} from "@/services/search/domainRows";

export default {
  name: "ProteinDomainRows",
  props: {
    value: { type: Array, required: true },
    error: { type: String, default: "" },
  },
  data() {
    return {
      maxRows: MAX_DOMAIN_ROWS,
    };
  },
  computed: {
    readings() {
      return this.value.map(readingFor);
    },
  },
  methods: {
    kindOf(row) {
      return domainTextKind(row.text);
    },
    // A name has no copies to count: it is held or, with "none", not.
    comparisonsFor(row) {
      return this.kindOf(row) === "name"
        ? COMPARISONS.filter((c) => c.value === "atLeast" || c.value === "none")
        : COMPARISONS;
    },
    connector(row) {
      if (row.comparison === "none") return "of";
      return Number(row.count) === 1 ? "copy of" : "copies of";
    },
    update(index, change) {
      const rows = this.value.map((row, i) => (i === index ? { ...row, ...change } : row));
      // Typing a name over an ID that was being counted leaves nothing to count.
      if (this.kindOf(rows[index]) === "name") {
        rows[index].count = 1;
        if (rows[index].comparison !== "none") rows[index].comparison = "atLeast";
      }
      this.$emit("input", rows);
    },
    add() {
      this.$emit("input", [...this.value, newDomainRow()]);
      this.$nextTick(() => {
        const field = document.getElementById(`domain_text_${this.value.length - 1}`);
        if (field) field.focus();
      });
    },
    remove(index) {
      this.$emit("input", this.value.filter((_, i) => i !== index));
    },
  },
};
</script>

<style scoped lang="scss">
.form-label {
  font-size: 18px;
  color: #777;
  font-weight: bold;
}
.form-section-group {
  border: none;
  margin: 0;
  padding: 0;
  margin-top: 1rem;
  margin-bottom: 1rem;
}
.section-legend {
  font-size: 1.25rem;
  font-weight: bold;
  color: black;
  margin-bottom: 0.5rem;
  padding: 0;
  border: none;
  width: auto;
  display: flex;
  align-items: center;
}
.legend-note {
  font-size: 0.9rem;
  font-weight: normal;
  color: #555;
  margin-left: 0.5rem;
}
.domain-row .row {
  margin-bottom: 0.25rem;
}
.domain-row {
  margin-bottom: 1rem;
}
.comparison {
  width: 120px;
  margin-right: 12px;
}
.count {
  width: 64px;
  margin-right: 12px;
}
.connector {
  margin-right: 12px;
}
.text {
  width: 100%;
  max-width: 420px;
}
.reading {
  font-size: 0.85rem;
  color: #555;
  text-align: left;
}
.reading-warning {
  display: block;
  color: #8a5a00;
}
.domain-error {
  color: #b00020;
  margin-top: 0.25rem;
}
</style>
